import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'

import { ChartType } from 'components/chart-v2/model'
import { createChartConfig } from 'components/chart-v2/utils'

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartDataLabels,
)

import type { DataPoint } from 'components/chart-v2/model'

type Props = {
  data?: DataPoint[]
  type?: ChartType
  chartOptions?: Record<string, unknown>
  colorMap?: Record<string, string>
}

const ChartV2: React.FC<Props> = ({ data, type, chartOptions, colorMap }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const chartRef = useRef<ChartJS | null>(null)

  useEffect(() => {
    if (!canvasRef.current) return

    // Destroy existing chart if it exists
    if (chartRef.current) {
      chartRef.current.destroy()
    }

    const config = createChartConfig(
      data ?? [],
      type ?? 'bar',
      {
        maintainAspectRatio: false,
        responsive: true,
        ...chartOptions,
      },
      colorMap,
    )

    chartRef.current = new ChartJS(canvasRef.current, config)

    // Cleanup function
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy()
        chartRef.current = null
      }
    }
  }, [data, type, chartOptions, colorMap])

  return (
    <SimpleContainer>
      <canvas ref={canvasRef} />
    </SimpleContainer>
  )
}

export default ChartV2

const SimpleContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`
